import React, { useEffect, useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lightbulb, ChevronUp, Plus, Upload, X, FileText, Paperclip, Sparkles, PlusCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "");

const STATUS_META = {
  new: { label: "New", cls: "bg-[#1E3F33]/10 text-[#5C6B64]" },
  reviewing: { label: "Reviewing", cls: "bg-[#B98900]/15 text-[#8a6800]" },
  planned: { label: "Planned", cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]" },
  done: { label: "Done", cls: "bg-[#2D6A4F] text-white" },
  declined: { label: "Declined", cls: "bg-[#B91C1C]/10 text-[#B91C1C]" },
};
const PRIORITY_META = {
  high: { label: "High urgency", cls: "text-[#B91C1C]" },
  medium: { label: "Medium urgency", cls: "text-[#8a6800]" },
  low: { label: "Low urgency", cls: "text-[#5C6B64]" },
};

function getAnonId() {
  let id = localStorage.getItem("ssm_anon_id");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("ssm_anon_id", id);
  }
  return id;
}

function SubmitDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("feature");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const addFiles = (e) => {
    setFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
    if (inputRef.current) inputRef.current.value = "";
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const reset = () => { setCategory("feature"); setTitle(""); setDescription(""); setPriority(""); setFiles([]); };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Please add a short title"); return; }
    if (!description.trim()) { toast.error("Please describe your suggestion"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("title", title);
      fd.append("description", description);
      if (priority) fd.append("priority", priority);
      files.forEach((f) => fd.append("files", f));
      await api.post("/suggestions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thanks! Your suggestion has been submitted.");
      setOpen(false); reset(); onDone && onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="suggestion-open-submit" className="btn-primary inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Share an idea
        </button>
      </DialogTrigger>
      <DialogContent data-testid="suggestion-submit-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#1E3F33] flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-[#CC5A3A]" /> Suggest an improvement
          </DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            Tell us what would make Screening Services Mobile better — a new feature or a new service type for the marketplace.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[{ k: "feature", label: "Feature idea", icon: Sparkles }, { k: "service", label: "New service type", icon: PlusCircle }].map((c) => (
              <button
                key={c.k}
                type="button"
                data-testid={`suggestion-category-${c.k}`}
                onClick={() => setCategory(c.k)}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors ${
                  category === c.k ? "border-[#CC5A3A] bg-[#CC5A3A]/5 text-[#CC5A3A]" : "border-[#1E3F33]/15 text-[#5C6B64] hover:border-[#1E3F33]/40"
                }`}
              >
                <c.icon className="w-4 h-4" /> {c.label}
              </button>
            ))}
          </div>
          <input
            data-testid="suggestion-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={140}
            placeholder={category === "service" ? "e.g. Hair follicle drug testing" : "e.g. Let me filter collectors by rating"}
            className="input-soft"
          />
          <textarea
            data-testid="suggestion-description-input"
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your idea and why it would help…"
            className="input-soft"
          />
          <div>
            <div className="text-xs font-medium text-[#1E3F33] mb-1.5">How urgent does this feel to you? (optional)</div>
            <div className="flex gap-2">
              {["low", "medium", "high"].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`suggestion-priority-${p}`}
                  onClick={() => setPriority(priority === p ? "" : p)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors capitalize ${
                    priority === p ? "bg-[#1E3F33] text-white border-[#1E3F33]" : "bg-white text-[#1E3F33] border-[#1E3F33]/15 hover:border-[#1E3F33]/40"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <input ref={inputRef} data-testid="suggestion-file-input" type="file" multiple accept="application/pdf,image/*" onChange={addFiles} className="hidden" />
            <button type="button" data-testid="suggestion-attach" onClick={() => inputRef.current?.click()} className="btn-secondary w-full inline-flex items-center justify-center gap-2 text-sm">
              <Upload className="w-4 h-4" /> Attach a screenshot (optional)
            </button>
            {files.length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid="suggestion-file-list">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-xs text-[#1E3F33]">
                    <span className="inline-flex items-center gap-1.5 truncate"><FileText className="w-3.5 h-3.5 shrink-0" /> {f.name}</span>
                    <button type="button" onClick={() => removeFile(i)} className="text-[#8A9A86] hover:text-[#B91C1C] shrink-0"><X className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button type="submit" data-testid="suggestion-submit" disabled={busy} className="btn-primary w-full">
            {busy ? "Submitting…" : "Submit suggestion"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SuggestionCard({ s, onVote }) {
  const status = STATUS_META[s.status] || STATUS_META.new;
  const prio = PRIORITY_META[s.priority];
  return (
    <div className="card-soft flex gap-4" data-testid={`suggestion-${s.id}`}>
      <button
        data-testid={`suggestion-vote-${s.id}`}
        onClick={() => onVote(s.id)}
        className={`shrink-0 flex flex-col items-center justify-center w-14 rounded-xl border transition-colors ${
          s.my_vote ? "border-[#CC5A3A] bg-[#CC5A3A]/10 text-[#CC5A3A]" : "border-[#1E3F33]/15 text-[#5C6B64] hover:border-[#CC5A3A] hover:text-[#CC5A3A]"
        }`}
        aria-pressed={s.my_vote}
      >
        <ChevronUp className="w-5 h-5" strokeWidth={2.2} />
        <span className="font-heading text-lg leading-none" data-testid={`suggestion-votes-${s.id}`}>{s.votes}</span>
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-[#CC5A3A] bg-[#CC5A3A]/10 rounded-full px-2 py-0.5">{s.category_label}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${status.cls}`} data-testid={`suggestion-status-${s.id}`}>{status.label}</span>
          {prio && <span className={`text-[10px] font-medium ${prio.cls}`}>· {prio.label}</span>}
        </div>
        <h3 className="font-heading text-lg text-[#1E3F33] mt-1.5">{s.title}</h3>
        <p className="text-sm text-[#5C6B64] mt-1 whitespace-pre-wrap break-words">{s.description}</p>
        {(s.attachments || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {s.attachments.map((a, i) => (
              <a key={i} href={`${API}/files/${a.file_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#CC5A3A] hover:underline border border-[#1E3F33]/10 rounded-lg px-2.5 py-1.5">
                <Paperclip className="w-3.5 h-3.5" /> {a.original_name || `Attachment ${i + 1}`}
              </a>
            ))}
          </div>
        )}
        {s.reply && (
          <div className="mt-3 rounded-xl bg-[#F0EFEA] p-3" data-testid={`suggestion-reply-${s.id}`}>
            <div className="text-xs font-medium text-[#2D6A4F] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Response from the team</div>
            <p className="text-sm text-[#1E3F33] mt-1 whitespace-pre-wrap">{s.reply}</p>
          </div>
        )}
        <div className="text-[11px] text-[#8A9A86] mt-2">{fmt(s.created_at)}</div>
      </div>
    </div>
  );
}

export default function Suggestions() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [sort, setSort] = useState("top");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = { sort, anon_id: getAnonId() };
    if (category !== "all") params.category = category;
    api.get("/suggestions", { params })
      .then(({ data }) => setItems(data.items))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [sort, category]); // eslint-disable-line

  const vote = async (id) => {
    // optimistic
    setItems((prev) => prev.map((s) => s.id === id ? { ...s, my_vote: !s.my_vote, votes: s.votes + (s.my_vote ? -1 : 1) } : s));
    try {
      await api.post(`/suggestions/${id}/vote`, null, { params: { anon_id: getAnonId() } });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      load();
    }
  };

  return (
    <div className="container-x py-12" data-testid="suggestions-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <Lightbulb className="w-7 h-7 text-[#CC5A3A]" /> Suggestions
          </h1>
          <p className="text-[#5C6B64] mt-2 max-w-xl">
            Have an idea to improve the platform or a service type you'd like added? Share it and upvote what matters most to you. {user ? "" : "No account needed."}
          </p>
        </div>
        <SubmitDialog onDone={load} />
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2">
        {[{ k: "all", label: "All" }, { k: "feature", label: "Feature ideas" }, { k: "service", label: "New service types" }].map((c) => (
          <button
            key={c.k}
            data-testid={`suggestions-filter-${c.k}`}
            onClick={() => setCategory(c.k)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${category === c.k ? "bg-[#1E3F33] text-white border-[#1E3F33]" : "bg-white text-[#1E3F33] border-[#1E3F33]/15 hover:border-[#1E3F33]/40"}`}
          >
            {c.label}
          </button>
        ))}
        <div className="ml-auto">
          <select data-testid="suggestions-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="input-soft text-sm py-2">
            <option value="top">Most upvoted</option>
            <option value="new">Newest</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-[#5C6B64]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 text-center py-16 rounded-2xl border border-dashed border-[#1E3F33]/15" data-testid="suggestions-empty">
          <Lightbulb className="w-10 h-10 text-[#CC5A3A]/50 mx-auto" />
          <p className="mt-3 text-[#5C6B64]">No suggestions yet — be the first to share an idea!</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map((s) => <SuggestionCard key={s.id} s={s} onVote={vote} />)}
        </div>
      )}
    </div>
  );
}
